import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, EyeOff, Fingerprint, KeyRound, ArrowLeft, ArrowRight, CheckCircle2, Activity, Globe2, Server } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Label } from '../components/Label';
import { Card, CardContent } from '../components/Card';
import { toast } from 'sonner';
import { Toaster } from '../components/Sonner';
import { PaspampresLogo } from '../components/dashboard/PaspampresLogo';
type Role = {
  id: string;
  label: string;
  code: string;
  username: string;
  password: string;
  email: string;
  initials: string;
  description: string;
};
export const ROLES: Role[] = [{
  id: 'presiden',
  label: 'Presiden RI',
  code: 'VVIP-01',
  username: 'presiden.ri',
  password: 'GarudaShield#2026',
  email: 'presiden.ri@gov.id',
  initials: 'RI',
  description: 'Akses penuh seluruh modul C4I'
}, {
  id: 'pimpinan',
  label: 'Pimpinan Pengamanan',
  code: 'SEC-LEAD',
  username: 'sec.lead',
  password: 'SecLead#2026',
  email: 'pimpinan.amankam@gov.id',
  initials: 'PP',
  description: 'Threat, Digital Escort, Visit Intel'
}, {
  id: 'command',
  label: 'Kepala Command Center',
  code: 'CC-01',
  username: 'kepala.cc',
  password: 'CmdCenter#2026',
  email: 'command.center@bssn.go.id',
  initials: 'CC',
  description: 'Tasking, Response, Operasi'
}, {
  id: 'analis',
  label: 'Analis Intelijen',
  code: 'A-12',
  username: 'analis.a12',
  password: 'Analyst#2026',
  email: 'analis.intel@bin.go.id',
  initials: 'AI',
  description: 'Workbench, OSINT, Investigasi'
}, {
  id: 'komunikasi',
  label: 'Tim Komunikasi',
  code: 'KOM-04',
  username: 'tim.komunikasi',
  password: 'Komun#2026',
  email: 'komunikasi@kominfo.go.id',
  initials: 'TK',
  description: 'Press, narasi, klarifikasi publik'
}, {
  id: 'kementerian',
  label: 'Kementerian / Lembaga',
  code: 'KL-22',
  username: 'liaison.kl',
  password: 'Kementerian#2026',
  email: 'liaison.kl@gov.id',
  initials: 'KL',
  description: 'Akses sektoral terbatas'
}, {
  id: 'pemda',
  label: 'Pemerintah Daerah',
  code: 'PEMDA-14',
  username: 'pemda.liaison',
  password: 'Pemda#2026',
  email: 'pemda.liaison@daerah.go.id',
  initials: 'PD',
  description: 'Geospasial regional'
}, {
  id: 'auditor',
  label: 'Auditor',
  code: 'AUD-09',
  username: 'auditor.bpk',
  password: 'Audit#2026',
  email: 'audit@bpk.go.id',
  initials: 'AU',
  description: 'Read-only + Audit Log'
}];
export function Login() {
  const nav = useNavigate();
  const [step, setStep] = useState<'creds' | 'otp'>('creds');
  const [roleId, setRoleId] = useState<string>('presiden');
  const role = ROLES.find(r => r.id === roleId) ?? ROLES[0];
  const [username, setUsername] = useState(role.username);
  const [password, setPassword] = useState(role.password);
  const [otpDigits, setOtpDigits] = useState<string[]>(['4', '8', '2', '9', '1', '3']);
  const [showPw, setShowPw] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);
  // When role changes, auto-fill matching demo credentials
  const selectRole = (id: string) => {
    setRoleId(id);
    const r = ROLES.find(x => x.id === id) ?? ROLES[0];
    setUsername(r.username);
    setPassword(r.password);
  };
  const persistSession = (r: Role) => {
    try {
      localStorage.setItem('gs_auth', JSON.stringify({
        roleId: r.id,
        role: r.label,
        code: r.code,
        username: r.username,
        email: r.email,
        initials: r.initials,
        ts: Date.now()
      }));
    } catch {}
  };
  const otp = otpDigits.join('');
  const handleInstantDemo = () => {
    persistSession(role);
    toast.success(`Mode demo · ${role.label}`, {
      description: 'Melewati MFA, langsung masuk Situation Room...'
    });
    setTimeout(() => nav('/dashboard'), 400);
  };
  const handleCreds = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Lengkapi kredensial');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep('otp');
      setResendIn(60);
      toast.success('Kredensial diverifikasi', {
        description: `Login sebagai ${role.label} (${role.code}). Kode OTP dikirim ke perangkat MFA.`
      });
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }, 700);
  };
  const handleOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      toast.error('Masukkan 6 digit OTP');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      persistSession(role);
      toast.success(`Autentikasi berhasil · ${role.label}`, {
        description: 'Memasuki Situation Room...'
      });
      nav('/dashboard');
    }, 700);
  };
  const updateOtpDigit = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setOtpDigits(prev => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  };
  const onOtpKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) otpRefs.current[idx + 1]?.focus();
  };
  const onOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 0) return;
    e.preventDefault();
    const next = [...otpDigits];
    for (let i = 0; i < 6; i++) next[i] = text[i] || '';
    setOtpDigits(next);
    const focusIdx = Math.min(text.length, 5);
    otpRefs.current[focusIdx]?.focus();
  };
  const onPwKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(e.getModifierState && e.getModifierState('CapsLock'));
  };
  const timeStr = now.toLocaleTimeString('id-ID', {
    hour12: false
  });
  return <div className="min-h-screen bg-background text-foreground flex relative overflow-hidden" data-id="element-2289">
      <Toaster theme="light" position="bottom-right" richColors data-id="element-2290" />

      {/* Background ambient */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,oklch(0.2_0.02_250/0.05)_1px,transparent_1px),linear-gradient(to_bottom,oklch(0.2_0.02_250/0.05)_1px,transparent_1px)] bg-[size:36px_36px]" data-id="element-2291" />
      <div className="pointer-events-none absolute -top-32 -left-32 size-[480px] rounded-full bg-primary/10 blur-3xl" data-id="element-2292" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 size-[520px] rounded-full bg-primary/5 blur-3xl" data-id="element-2293" />

      {/* Left visual panel */}
      <div className="hidden lg:flex lg:w-1/2 relative border-r border-border/60 items-center justify-center overflow-hidden" data-id="element-2294">
        <div className="absolute top-0 inset-x-0 h-9 border-b border-border/60 bg-card backdrop-blur flex items-center justify-between px-5 font-mono text-[10px] uppercase tracking-widest" data-id="element-2295">
          <div className="flex items-center gap-2 text-emerald-600" data-id="element-2296">
            <span className="relative flex size-1.5" data-id="element-2297">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" data-id="element-2298" />
              <span className="relative inline-flex size-1.5 rounded-full bg-emerald-500" data-id="element-2299" />
            </span>
            System Operational
          </div>
          <div className="text-muted-foreground" data-id="element-2300">
            Zero Trust · Air-Gapped · BSSN
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-9 border-t border-border/60 bg-card backdrop-blur flex items-center justify-between px-5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-2301">
          <span data-id="element-2302">Rahasia · Eyes Only · UU PDP 27/2022 · UU ITE</span>
          <span className="text-foreground/70" data-id="element-2303">v4.1.0 · build 2026.05</span>
        </div>

        <div className="relative text-center max-w-md px-8 py-12" data-id="element-2304">
          <div className="relative mx-auto mb-7" data-id="element-2305">
            <div className="absolute inset-0 -m-6 rounded-full bg-gradient-to-br from-amber-100/40 via-red-100/30 to-emerald-100/40 blur-2xl" data-id="element-2306" />
            <div className="relative size-32 mx-auto rounded-full bg-white border-2 border-border/60 flex items-center justify-center shadow-xl" data-id="element-2307">
              <PaspampresLogo size={108} className="size-28" data-id="element-2308" />
            </div>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-1 font-bold" data-id="element-2309">
            Pasukan Pengamanan Presiden
          </p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2" data-id="element-2310">
            GARUDA SHIELD <span className="text-primary" data-id="element-2311">C4I</span>
          </h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2" data-id="element-2312">
            Presidential Cyber Situation Room
          </p>
          <p className="font-heading italic text-sm text-foreground/70 mb-5" data-id="element-2313">
            “Setia · Waspada”
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-2314">
            Sistem Pemantauan Pemberitaan Siber, Narasi Nasional, dan Stabilitas
            Strategis untuk Pengamanan Presiden Republik Indonesia.
          </p>

          <div className="grid grid-cols-3 gap-3 mt-8" data-id="element-2315">
            {[{
            icon: Lock,
            label: 'Zero Trust'
          }, {
            icon: Fingerprint,
            label: 'MFA Wajib'
          }, {
            icon: Shield,
            label: 'Air-Gapped'
          }].map(b => <div key={b.label} className="p-3 rounded-lg border border-border/60 bg-card backdrop-blur" data-id="element-2316">
                <b.icon className="size-4 text-primary mx-auto mb-1.5" data-id="element-2317" />
                <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-2318">
                  {b.label}
                </p>
              </div>)}
          </div>

          <div className="mt-8 rounded-lg border border-border/60 bg-card backdrop-blur p-4 text-left" data-id="element-2319">
            <div className="flex items-center justify-between mb-3" data-id="element-2320">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-2321">
                Session Context
              </div>
              <div className="font-mono text-[10px] text-emerald-600" data-id="element-2322">LIVE</div>
            </div>
            <div className="space-y-2 text-xs" data-id="element-2323">
              <div className="flex items-center justify-between" data-id="element-2324">
                <span className="flex items-center gap-2 text-muted-foreground" data-id="element-2325">
                  <Activity className="size-3.5" data-id="element-2326" /> Waktu Server
                </span>
                <span className="font-mono text-foreground" data-id="element-2327">{timeStr} WIB</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2328">
                <span className="flex items-center gap-2 text-muted-foreground" data-id="element-2329">
                  <Globe2 className="size-3.5" data-id="element-2330" /> Region
                </span>
                <span className="font-mono text-foreground" data-id="element-2331">
                  JKT-DC1 / SBY-DC2
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2332">
                <span className="flex items-center gap-2 text-muted-foreground" data-id="element-2333">
                  <Server className="size-3.5" data-id="element-2334" /> Threat Level
                </span>
                <span className="font-mono text-amber-600" data-id="element-2335">ELEVATED</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 relative" data-id="element-2336">
        <div className="w-full max-w-md" data-id="element-2337">
          <div className="lg:hidden flex items-center gap-3 mb-6" data-id="element-2338">
            <div className="size-12 rounded-xl bg-white border border-border/60 flex items-center justify-center shadow-sm shrink-0" data-id="element-2339">
              <PaspampresLogo size={40} className="size-10" data-id="element-2340" />
            </div>
            <div className="min-w-0" data-id="element-2341">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-amber-700 font-bold" data-id="element-2342">
                Paspampres
              </p>
              <h1 className="font-heading text-lg font-bold leading-tight" data-id="element-2343">
                GARUDA SHIELD <span className="text-primary" data-id="element-2344">C4I</span>
              </h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate" data-id="element-2345">
                Cyber Situation Room
              </p>
            </div>
          </div>

          <Card className="bg-card border-border/60 backdrop-blur-xl shadow-2xl shadow-black/10" data-id="element-2346">
            <CardContent className="p-6 sm:p-8" data-id="element-2347">
              <div className="flex items-center gap-2 mb-5" data-id="element-2348">
                <StepDot active={step === 'creds'} done={step === 'otp'} label="Kredensial" n={1} data-id="element-2349" />
                <div className="flex-1 h-px bg-border/60" data-id="element-2350" />
                <StepDot active={step === 'otp'} done={false} label="Verifikasi MFA" n={2} data-id="element-2351" />
              </div>

              <div className="mb-6" data-id="element-2352">
                <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary mb-3 px-2 py-0.5 rounded bg-primary/10 border border-primary/20" data-id="element-2353">
                  <Lock className="size-3" data-id="element-2354" /> Akses Terbatas
                </div>
                <h2 className="font-heading text-2xl font-bold" data-id="element-2355">
                  {step === 'creds' ? 'Masuk ke Sistem' : 'Verifikasi MFA'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1" data-id="element-2356">
                  {step === 'creds' ? `Login sebagai: ${role.label}. Pilih peran lain untuk mengganti.` : `MFA untuk ${role.label} (${role.code}).`}
                </p>
              </div>

              {step === 'creds' ? <form onSubmit={handleCreds} className="space-y-4" data-id="element-2357">
                  {/* Role picker */}
                  <div data-id="element-2358">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground" data-id="element-2359">
                      Peran ({ROLES.length} opsi)
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-[260px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border" data-id="element-2360">
                      {ROLES.map(r => {
                    const active = roleId === r.id;
                    return <button type="button" key={r.id} onClick={() => selectRole(r.id)} className={`relative p-2.5 rounded-lg border text-left transition ${active ? 'bg-primary/10 border-primary/50 text-foreground ring-1 ring-primary/30' : 'bg-muted/30 border-border/60 hover:bg-muted/50 hover:border-border'}`} aria-pressed={active} data-id="element-2361">
                            <div className="flex items-start justify-between gap-2" data-id="element-2362">
                              <div className="min-w-0" data-id="element-2363">
                                <p className="text-xs font-bold leading-tight truncate" data-id="element-2364">
                                  {r.label}
                                </p>
                                <p className="font-mono text-[9px] text-muted-foreground mt-0.5 truncate" data-id="element-2365">
                                  {r.code}
                                </p>
                              </div>
                              {active && <CheckCircle2 className="size-3.5 text-primary shrink-0" data-id="element-2366" />}
                            </div>
                          </button>;
                  })}
                    </div>
                    <p className="mt-1.5 font-mono text-[10px] text-muted-foreground leading-relaxed" data-id="element-2367">
                      <span className="text-foreground/80" data-id="element-2368">
                        Peran terpilih:
                      </span>{' '}
                      {role.label} — {role.description}
                    </p>
                  </div>

                  {/* Username */}
                  <div data-id="element-2369">
                    <Label htmlFor="username" className="text-xs font-mono uppercase tracking-wider text-muted-foreground" data-id="element-2370">
                      Username / NIP
                    </Label>
                    <Input id="username" value={username} onChange={e => setUsername(e.target.value)} placeholder={`cth: ${role.username}`} className="mt-1.5 bg-muted/30 border-border/60 focus-visible:border-primary/60 font-mono" autoComplete="username" data-id="element-2371" />
                  </div>

                  {/* Password */}
                  <div data-id="element-2372">
                    <div className="flex items-center justify-between" data-id="element-2373">
                      <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground" data-id="element-2374">
                        Password
                      </Label>
                      <button type="button" onClick={() => toast.info('Hubungi BSSN Helpdesk untuk reset password', {
                    description: '0800-GARUDA (4278-32)'
                  })} className="text-[10px] text-primary hover:underline" data-id="element-2375">
                        Lupa password?
                      </button>
                    </div>
                    <div className="relative mt-1.5" data-id="element-2376">
                      <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onKeyUp={onPwKeyUp} placeholder="••••••••••••" className="bg-muted/30 border-border/60 focus-visible:border-primary/60 pr-10 font-mono" autoComplete="current-password" data-id="element-2377" />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition" aria-label={showPw ? 'Sembunyikan password' : 'Tampilkan password'} data-id="element-2378">
                        {showPw ? <EyeOff className="size-4" data-id="element-2379" /> : <Eye className="size-4" data-id="element-2380" />}
                      </button>
                    </div>
                    {capsLock && <p className="mt-1.5 text-[10px] font-mono text-amber-600 flex items-center gap-1" data-id="element-2381">
                        ⚠ Caps Lock aktif
                      </p>}
                  </div>

                  <Button type="submit" disabled={loading} className="w-full group" data-id="element-2382">
                    {loading ? 'Memverifikasi...' : <>
                        Lanjutkan ke MFA
                        <ArrowRight className="size-4 ml-1.5 transition group-hover:translate-x-0.5" data-id="element-2383" />
                      </>}
                  </Button>

                  <div className="relative my-1" data-id="element-2384">
                    <div className="absolute inset-0 flex items-center" data-id="element-2385">
                      <span className="w-full border-t border-border/60" data-id="element-2386" />
                    </div>
                    <div className="relative flex justify-center" data-id="element-2387">
                      <span className="bg-card px-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground" data-id="element-2388">
                        atau
                      </span>
                    </div>
                  </div>

                  <Button type="button" variant="outline" className="w-full border-border/60 bg-muted/20 hover:bg-muted/40" onClick={handleInstantDemo} data-id="element-2389">
                    <KeyRound className="size-3.5 mr-1.5 text-primary" data-id="element-2390" />
                    Masuk Instan sebagai {role.label}
                  </Button>

                  <p className="font-mono text-[10px] text-center text-muted-foreground leading-relaxed" data-id="element-2391">
                    Demo otomatis diisi sesuai peran yang dipilih. <br data-id="element-2392" />
                    Tekan <b className="text-foreground/80" data-id="element-2393">Lanjutkan</b> untuk
                    alur lengkap, atau{' '}
                    <b className="text-foreground/80" data-id="element-2394">Masuk Instan</b> untuk
                    lewati MFA.
                  </p>
                </form> : <form onSubmit={handleOtp} className="space-y-5" data-id="element-2395">
                  <div className="flex items-start gap-2.5 p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs" data-id="element-2396">
                    <KeyRound className="size-4 text-primary shrink-0 mt-0.5" data-id="element-2397" />
                    <div className="text-foreground/80 leading-relaxed" data-id="element-2398">
                      Kode dikirim ke{' '}
                      <span className="font-mono text-primary" data-id="element-2399">YubiKey</span> /
                      Authenticator. Berlaku{' '}
                      <span className="font-mono" data-id="element-2400">60 detik</span>.
                    </div>
                  </div>

                  <div data-id="element-2401">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground" data-id="element-2402">
                      Kode OTP (6 Digit)
                    </Label>
                    <div className="mt-2 grid grid-cols-6 gap-2" onPaste={onOtpPaste} data-id="element-2403">
                      {otpDigits.map((d, i) => <input key={i} ref={el => otpRefs.current[i] = el} value={d} onChange={e => updateOtpDigit(i, e.target.value)} onKeyDown={e => onOtpKey(i, e)} inputMode="numeric" maxLength={1} aria-label={`Digit OTP ${i + 1}`} className="h-12 sm:h-14 rounded-lg bg-muted/30 border border-border/60 focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none text-center font-mono text-xl sm:text-2xl font-bold transition" data-id="element-2404" />)}
                    </div>
                  </div>

                  <div className="flex gap-2" data-id="element-2405">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('creds')} data-id="element-2406">
                      <ArrowLeft className="size-4 mr-1.5" data-id="element-2407" />
                      Kembali
                    </Button>
                    <Button type="submit" disabled={loading || otp.length < 6} className="flex-[1.4]" data-id="element-2408">
                      {loading ? 'Verifikasi...' : <>
                          Masuk sebagai {role.label}
                          <ArrowRight className="size-4 ml-1.5" data-id="element-2409" />
                        </>}
                    </Button>
                  </div>

                  <div className="text-center" data-id="element-2410">
                    {resendIn > 0 ? <p className="font-mono text-[10px] text-muted-foreground" data-id="element-2411">
                        Kirim ulang kode dalam{' '}
                        <span className="text-foreground" data-id="element-2412">{resendIn}s</span>
                      </p> : <button type="button" onClick={() => {
                  setResendIn(60);
                  toast.info('OTP baru dikirim');
                }} className="text-[11px] text-primary hover:underline font-mono" data-id="element-2413">
                        Kirim ulang kode OTP
                      </button>}
                  </div>
                </form>}

              <div className="mt-6 pt-4 border-t border-border/60" data-id="element-2414">
                <p className="font-mono text-[9px] text-muted-foreground text-center leading-relaxed" data-id="element-2415">
                  Setiap upaya akses tercatat. Penyalahgunaan sistem dapat
                  dipidana sesuai UU ITE No. 11/2008 & UU PDP No. 27/2022.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="lg:hidden mt-4 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-muted-foreground px-1" data-id="element-2416">
            <span data-id="element-2417">{timeStr} WIB</span>
            <span data-id="element-2418">v4.1.0 · build 2026.05</span>
          </div>
        </div>
      </div>
    </div>;
}
function StepDot({
  active,
  done,
  label,
  n
}: {
  active: boolean;
  done: boolean;
  label: string;
  n: number;
}) {
  return <div className="flex items-center gap-2" data-id="element-2419">
      <div className={`size-6 rounded-full flex items-center justify-center font-mono text-[10px] font-bold border transition ${done ? 'bg-primary border-primary text-primary-foreground' : active ? 'bg-primary/15 border-primary text-primary' : 'bg-muted/30 border-border/60 text-muted-foreground'}`} data-id="element-2420">
        {done ? <CheckCircle2 className="size-3.5" data-id="element-2421" /> : n}
      </div>
      <span className={`font-mono text-[10px] uppercase tracking-widest hidden sm:inline ${active || done ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-2422">
        {label}
      </span>
    </div>;
}